import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";

import { useT } from "../i18n";

/**
 * Os recados que aparecem num canto e somem sozinhos.
 *
 * Antes desta pilha, cada tela guardava o próprio erro e desenhava um parágrafo
 * vermelho no lugar que desse. Eram sete telas com o mesmo estado e mais de dez
 * cópias do mesmo `<p role="alert">` com o mesmo estilo escrito à mão, cada uma
 * num ponto diferente da página. O resultado é que o mesmo erro aparecia em
 * cima numa tela, embaixo noutra, e empurrava o botão que a pessoa ia clicar.
 *
 * **Uma pilha, e não um aviso.** Dois erros seguidos com um único lugar viram
 * um erro só: o segundo apaga o primeiro antes de alguém ler. Acontece
 * exatamente quando mais importa, que é quando alguma coisa está falhando em
 * série.
 *
 * **O que é de campo continua no campo.** Sigla repetida e nome recusado
 * pertencem ao lado do campo que os causou, e mandá-los para o canto da tela
 * obrigaria a pessoa a ligar um recado distante à caixa que ela está
 * preenchendo. Aqui entra o que é resposta de ação: limite de pedidos, rede
 * fora, permissão negada, compra concluída.
 */

/** O que o recado é, e é só isso que muda a cor. */
export type TipoDeAviso = "erro" | "recado" | "feito";

interface Aviso {
  id: number;
  texto: string;
  tipo: TipoDeAviso;
}

/** Quanto tempo cada recado fica antes de sair sozinho. */
const DURACAO_MS = 6000;

/**
 * Quantos cabem ao mesmo tempo.
 *
 * Acima disso a pilha vira parede e tapa o jogo. Quando chega um a mais, o
 * mais velho sai, que é o que já estava lá tempo suficiente para ser lido.
 */
const MAXIMO = 3;

const COR: Record<TipoDeAviso, string> = {
  erro: "var(--danger)",
  recado: "var(--warning)",
  feito: "var(--success)",
};

interface Balcao {
  avisar: (texto: string, tipo?: TipoDeAviso) => void;
}

const Contexto = createContext<Balcao | null>(null);

/**
 * O canto onde os recados aparecem.
 *
 * `useAvisos` fora deste provedor é erro de montagem e não de execução, então
 * ele falha alto em vez de devolver uma função que não faz nada: recado que
 * some calado é pior do que recado nenhum.
 */
export function useAvisos(): Balcao {
  const balcao = useContext(Contexto);
  if (!balcao) throw new Error("useAvisos precisa estar dentro de ProvedorDeAvisos");
  return balcao;
}

/**
 * Leva o erro que a tela já guarda para o canto.
 *
 * As telas continuam com o próprio estado de erro, porque é ele que decide
 * outras coisas, tipo desligar um botão enquanto a ação falhou. O que muda é
 * onde o texto aparece: sai do meio da página, onde empurrava o que estava
 * embaixo, e vai para a pilha.
 *
 * Só dispara quando o texto muda. Tentar de novo e falhar igual renova o
 * mesmo recado em vez de somar outro, que é o que a pilha já faz com texto
 * repetido.
 */
export function useErroEmToast(erro: string | null, tipo: TipoDeAviso = "erro"): void {
  const { avisar } = useAvisos();
  useEffect(() => {
    if (erro) avisar(erro, tipo);
  }, [erro, tipo, avisar]);
}

export function ProvedorDeAvisos({ children }: { children: ReactNode }): ReactNode {
  const [avisos, definirAvisos] = useState<Aviso[]>([]);
  // Contador próprio em vez de relógio: dois recados no mesmo milissegundo
  // dividiriam a chave e o React reaproveitaria o elemento errado.
  const proximo = useRef(0);

  const fechar = useCallback((id: number) => {
    definirAvisos((atuais) => atuais.filter((aviso) => aviso.id !== id));
  }, []);

  const avisar = useCallback((texto: string, tipo: TipoDeAviso = "erro") => {
    const limpo = texto.trim();
    if (limpo.length === 0) return;

    proximo.current += 1;
    const aviso = { id: proximo.current, texto: limpo, tipo };
    definirAvisos((atuais) => {
      /*
       * Recado repetido renova em vez de empilhar.
       *
       * Apertar o mesmo botão três vezes contra um limite de pedidos devolve o
       * mesmo texto três vezes, e três cópias iguais na tela não dizem nada que
       * uma não diga.
       */
      const semRepetido = atuais.filter((atual) => atual.texto !== limpo);
      return [...semRepetido, aviso].slice(-MAXIMO);
    });
  }, []);

  const balcao = useMemo(() => ({ avisar }), [avisar]);

  return (
    <Contexto.Provider value={balcao}>
      {children}
      <Pilha avisos={avisos} aoFechar={fechar} />
    </Contexto.Provider>
  );
}

function Pilha({
  avisos,
  aoFechar,
}: {
  avisos: Aviso[];
  aoFechar: (id: number) => void;
}): ReactNode {
  if (avisos.length === 0) return null;

  return (
    <div
      style={{
        position: "fixed",
        right: 16,
        /*
         * Canto de cima, e não o de baixo.
         *
         * Embaixo o recado disputa espaço com a barra de navegação, que no
         * celular fica presa ali, e com a barra de ação do jogo, que tem o
         * botão de avançar rodada. Em cima não há nada preso: a folga é só a
         * barra de status do aparelho, que `env(safe-area-inset-top)` resolve.
         */
        top: "calc(12px + env(safe-area-inset-top, 0px))",
        zIndex: 60,
        display: "flex",
        flexDirection: "column",
        gap: 8,
        // Sem isto a coluna inteira intercepta clique no meio da tela, mesmo
        // onde não há recado nenhum desenhado.
        pointerEvents: "none",
      }}
    >
      {avisos.map((aviso) => (
        <Recado key={aviso.id} aviso={aviso} aoFechar={() => aoFechar(aviso.id)} />
      ))}
    </div>
  );
}

function Recado({ aviso, aoFechar }: { aviso: Aviso; aoFechar: () => void }): ReactNode {
  const t = useT();
  const [saindo, definirSaindo] = useState(false);

  useEffect(() => {
    const relogio = window.setTimeout(() => {
      definirSaindo(true);
      // Espera a transição terminar antes de tirar do ar, senão o recado some
      // de um quadro para o outro e parece falha de desenho.
      window.setTimeout(aoFechar, 240);
    }, DURACAO_MS);
    return () => window.clearTimeout(relogio);
  }, [aoFechar]);

  return (
    <div
      /*
       * Erro interrompe o leitor de tela, recado não.
       *
       * `alert` corta o que está sendo lido, e é o certo para uma ação que
       * falhou: quem não enxerga a tela precisa saber na hora que o botão não
       * funcionou. Para confirmação e aviso, `status` diz a mesma coisa sem
       * atropelar ninguém.
       */
      role={aviso.tipo === "erro" ? "alert" : "status"}
      aria-live={aviso.tipo === "erro" ? "assertive" : "polite"}
      style={{
        pointerEvents: "auto",
        maxWidth: "min(380px, calc(100vw - 32px))",
        display: "flex",
        alignItems: "flex-start",
        gap: 10,
        padding: "11px 13px",
        borderRadius: 10,
        border: "1px solid var(--border)",
        borderLeft: `3px solid ${COR[aviso.tipo]}`,
        background: "var(--surface-2)",
        boxShadow: "0 8px 24px rgba(0, 0, 0, 0.32)",
        opacity: saindo ? 0 : 1,
        transform: saindo ? "translateY(-8px)" : "translateY(0)",
        transition: "opacity 220ms ease, transform 220ms ease",
      }}
    >
      <span style={{ fontSize: 13, lineHeight: 1.45, flex: 1 }}>{aviso.texto}</span>
      <button
        className="ghost fixo"
        onClick={aoFechar}
        aria-label={t("comum.fechar")}
        style={{ padding: "2px 7px", fontSize: 15, lineHeight: 1 }}
      >
        ×
      </button>
    </div>
  );
}
